import { Component, OnDestroy, OnInit } from '@angular/core';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { EbwModalService } from '@ebw-monorepo/modal';
import { UtilsService } from '@ebw-monorepo/utils';
import { API_URLS, ApiUrlService } from 'libs/ng-components/utils/src/lib/services/api-url.service';
import moment from 'moment';
import Layer from 'ol/layer/Layer';
import { ImageWMS, TileWMS } from 'ol/source';
import { BehaviorSubject, combineLatest, filter, firstValueFrom, Subject, Subscription, switchMap, timer } from 'rxjs';
import { MapService } from '../../services/map.service';

/**
 * Class for storing the configuration of a history layer
 */
export class TimelayerConf {
  name: string;
  id: number;
  historyField: string;
  visibility: boolean;
}

/**
 * Type of data stored in the layer map
 */
type LayerData = {
  name: string;
  label: string;
  id: number;
  field: string;
  dates: string[];
  currentDateIndex: number;
};

/**
 * Component for displaying a timeline of layer history.
 */
@Component({
  selector: 'ebw-monorepo-layer-history-timeline',
  templateUrl: './layer-history-timeline.component.html',
  styleUrls: ['./layer-history-timeline.component.scss']
})
export class LayerHistoryTimelineComponent implements OnInit, OnDestroy {
  // keeps track of whether the timeline should be shown or not
  public isVisible: boolean = false;
  public layerMap: Map<number, LayerData> = new Map<number, LayerData>();

  // id of the currently selected layer
  public currentLayerId: number = 0;
  // list of dates for the currently selected layer
  public currentDates: string[] = [];
  public currentDateIndex: number = 0;

  // flag to keep track if the autorun feature is active or not
  public autoRun: boolean = false;

  // URL of the api to get the available dates for the layer
  private _apiUrl: string = '';
  // counter of loading tiles, used to check if all tiles are loaded
  private _loadingTilesCounter: number = 0;
  // interval between autorun updates in milliseconds
  private _minPollingInterval: number = 1500;
  // subject to notify when all tiles are loaded
  private _tilesLoadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  // subject to loop over the autorun process
  private _autorunPollingSubject: Subject<void> = new Subject<void>();
  // subscription for the autorun polling process
  private _autorunPollingSub: Subscription;
  private _timelineSubscription: Subscription;

  constructor(
    private utilsService: UtilsService,
    private _mapService: MapService,
    private _ebwModalService: EbwModalService,
    private _i18nService: I18NService,
    private apiUrlService: ApiUrlService
  ) {}

  /* ----------------------------- Lifecycle Hooks ---------------------------- */
  ngOnInit(): void {
    // URL to get the available dates for the layer
    this._apiUrl = `${this.apiUrlService.createUrl(API_URLS.MAP.LAYER.LAYER_HYSTORY)}`; // TODO la rotta completa era /saas/layer/getDates. Va aggiunta nel nuovo map-microservice

    // listen to the timelines changes on the map service
    this._timelineSubscription = this._mapService.timelines$.subscribe(async (conf: TimelayerConf) => {
      // if no layer with a history was selected, return
      if (!conf) {
        return;
      }

      // stop any autorun
      this._clearAutoRunPolling();

      // if the layer is already present and it's being set to invisible
      if (this.layerMap.has(conf.id) && !conf.visibility) {
        // update the visibility of the layer and the events listening to it
        this._setVisibilityLayerAndToggleEvent(conf.name, false);

        // if the currently selected layer matches with the one being updated and there are more layers loaded
        if (this.currentLayerId === conf.id && this.layerMap.size !== 1) {
          // get the first layer in the map
          const firstLayer = this.layerMap.entries().next().value;
          // set it as the last changed layer (defaults it to currently selected layer)
          this.onLayerChange(firstLayer[0]);
        }

        // if the map only contains one layer, disable the UI
        if (this.layerMap.size === 1) {
          this.isVisible = false;
        }

        // delete the layer from the map
        this._deleteLayer(conf.id);

        return;
      }

      // add the layer to the map
      try {
        await this._addLayerToMap(conf);
      } catch (error) {
        console.error(error);
      }
    });
  }

  ngOnDestroy(): void {
    // stop the autorun if the feature component is closed
    this._clearAutoRunPolling();
    // unsubscribe from the timeline subscription and reset its content
    this._timelineSubscription.unsubscribe();
    this._mapService.timelines$.next(null);
  }

  /* ----------------------------- Public Methods ----------------------------- */
  /**
   * Method called when a layer changes
   *
   * @param {number} id ID of the layer that changed
   */
  public onLayerChange(id: number): void {
    // reset the autorun
    this._clearAutoRunPolling();

    // get the layer from the map
    const layer: LayerData = this.layerMap.get(id);
    // set it as the current layer
    this.currentDateIndex = layer.currentDateIndex;
    this.currentDates = layer.dates;
    this.currentLayerId = id;
  }

  /**
   * Method called when a date changes
   *
   * @param {number} index Index of the selected new date from the list
   * @param {boolean} clearInterval Flag to decide if to the autorun should be stopped or not. Default is true
   */
  public onDateChange(index: number, clearInterval: boolean = true): void {
    // stop the autorun if the flag is set to true
    if (clearInterval) {
      this._clearAutoRunPolling();
    }

    // get the data from the layer map of the current selected layer
    const data = this.layerMap.get(this.currentLayerId);
    // set the current date value to the index of the selected date
    this.currentDateIndex = index;

    // update the value of the current date back in the map
    this.layerMap.set(this.currentLayerId, Object.assign(data, { currentDateIndex: index }));

    // apply the cql filter to the layer based on the selected date and field
    this._mapService.setLayerCQL(data.name, data.dates[index], data.field);
  }

  /**
   * Method used in the template to format the date strings (requires arrow function)
   *
   * @param {number} value Index of the date to retrieve from the list
   * @returns {string} Date extracted from the list of dates
   */
  public getDate = (value: number): string => {
    // if the list of dates is empty, return an empty string
    if (!this.currentDates || this.currentDates.length == 0) {
      return '';
    }

    // return the date at the specified index in the list of dates
    return this.currentDates[value];
  };

  /**
   * Method for toggling the autorun feature on and off called by the template
   */
  public toggleAutoRun(): void {
    // toggle the autorun flag
    this.autoRun = !this.autoRun;
    // handle the change of the autorun feature
    this._hadleAutoRunTimer();
  }

  /* ----------------------------- Private Methods ---------------------------- */
  /**
   * Method for handling the autorun feature
   */
  private _hadleAutoRunTimer(): void {
    // if the autorun is enabled, start the polling process
    if (this.autoRun) {
      // get the current layer data from the map
      const data: LayerData = this.layerMap.get(this.currentLayerId);

      // get the starting index of the dates to cycle through (calculated as the current date index or 0 if it's the last date)
      const startIndex: number = this.currentDateIndex < data.dates.length - 1 ? this.currentDateIndex : 0;
      // get the ending index of the dates to cycle through (calculated as the last date index in the list)
      const endIndex: number = data.dates.length - 1;

      // subscribe to the autorun polling subject and start the polling process
      this._subAutorunPolling(startIndex, endIndex);

      // emit the first signal to start the polling process
      this._autorunPollingSubject.next();

      // start by considering the tiles as loaded
      this._tilesLoadedSubject.next(true);
    }
    // if the autorun is being disabled, clear the polling process
    else {
      this._clearAutoRunPolling();
    }
  }

  /**
   * Method for stopping the autorun feature
   */
  private _clearAutoRunPolling(): void {
    this.autoRun = false;
    this._unsubAutorunPolling();
  }

  /**
   * Method for deleting a layer from the map
   *
   * @param {number} id ID of the layer to delete
   */
  private _deleteLayer(id: number): void {
    this.layerMap.delete(id);
  }

  /**
   * Method for adding a layer to the map
   *
   * @param {TimelayerConf} conf Configuration object of the layer to add
   */
  private async _addLayerToMap(conf: TimelayerConf): Promise<void> {
    try {
      // make an HTTP request to get the list of available dates for the layer
      const datesResponse = await firstValueFrom(
        this.utilsService.postRequest(this._apiUrl, { id: conf.id, field: conf.historyField }, false)
      );

      // if there was an error in the response, show an error modal and return
      if (!datesResponse || datesResponse.error !== 0) {
        this._ebwModalService.openErrorModal({
          title: this._i18nService.getInstantValue('common.error'),
          content: this._i18nService.getInstantValue('common.data_retrieving_error')
        });
        console.log('error');
        return;
      }

      // get the list of dates from the response
      const dates: string[] = datesResponse.data[0].dates;

      // set the current layer data
      this.currentLayerId = conf.id;
      this.currentDateIndex = dates.length - 1;
      this.currentDates = dates.map((_) => moment(_).format('DD-MM-YYYY'));

      // add the layer to the map
      this.layerMap.set(conf.id, {
        label: this._normalizeLabel(conf.name),
        name: conf.name,
        id: conf.id,
        dates,
        currentDateIndex: dates.length - 1,
        field: conf.historyField
      });

      // apply the CQL filter to the layer based on the last date in the list and the field
      this._mapService.setLayerCQL(conf.name, dates[dates.length - 1], conf.historyField);
      // set the layer visibility to true and toggle the event listeners for tile loading
      this._setVisibilityLayerAndToggleEvent(conf.name, true);
      // enable the UI to be visible
      this.isVisible = true;
    } catch (error) {
      this._ebwModalService.openErrorModal({
        title: this._i18nService.getInstantValue('common.error'),
        content: this._i18nService.getInstantValue('common.data_retrieving_error')
      });
      console.error(error);
    }
  }

  /**
   * Method for switching the visibility of a layer and toggling the event listeners for tile loading
   *
   * @param {string} name Name of the layer to change visibility
   * @param {boolean} visibile New visibility setting for the layer
   */
  private _setVisibilityLayerAndToggleEvent(name: string, visibile: boolean): void {
    // update the visibility of the layer
    this._mapService.setLayerVisibility(name, visibile);

    // get the layer object by name
    const layerObject = this._mapService.activeLayers.get(name);

    // if a layer was found, toggle the event listeners for tile loading
    if (layerObject) {
      // extract the source of the layer
      const layerSource = (layerObject as Layer).getSource();
      // if the source is a TileWMS or an ImageWMS, add or remove the event listeners for tile loading
      if (layerSource instanceof TileWMS || layerSource instanceof ImageWMS) {
        // add the listeners
        if (visibile) {
          (layerSource as TileWMS).on('tileloadstart', this._tileStartCallback);
          (layerSource as TileWMS).on('tileloadend', this._tileloadendCallback);
          (layerSource as TileWMS).on('tileloaderror', this._tileloadendCallback);
        }
        // remove the listeners
        else {
          (layerSource as TileWMS).un('tileloadstart', this._tileStartCallback);
          (layerSource as TileWMS).un('tileloadend', this._tileloadendCallback);
          (layerSource as TileWMS).un('tileloaderror', this._tileloadendCallback);
        }
      }
    }
  }

  /**
   * Callback function for tile load end event
   */
  private _tileloadendCallback = (): void => {
    // decrement the loading tiles counter
    this._loadingTilesCounter--;
    this._checkIfAllTilesLoaded();
  };

  /**
   * Callback function for tile load start event
   */
  private _tileStartCallback = (): void => {
    // notify that the tiles are loading
    this._tilesLoadedSubject.next(false);
    // increment the loading tiles counter
    this._loadingTilesCounter++;
  };

  /**
   * Method to check if all tiles have been loaded
   */
  private _checkIfAllTilesLoaded(): void {
    // if there are no more tiles loading, emit the signal that all tiles are loaded
    if (this._loadingTilesCounter === 0) {
      this._tilesLoadedSubject.next(true);
    }
  }

  /**
   * Method to normalize the label by replacing underscores with spaces
   *
   * @param {string} label Label to normalise
   * @returns {string} Normalized label
   */
  private _normalizeLabel(label: string): string {
    return label.replace(/_/g, ' ');
  }

  /**
   * Method to start the autorun polling process
   *
   * @param {number} startIndex Index to start the autorun from
   * @param {number} endIndex Index to end the autorun at
   */
  private _subAutorunPolling(startIndex: number, endIndex: number): void {
    // subscribe to the autorun polling subject and start the polling process and store the subscription
    this._autorunPollingSub = this._autorunPollingSubject
      .pipe(
        // proceed only if the tiles are loaded the timer ended
        switchMap(() => combineLatest([this._tilesLoadedSubject, timer(this._minPollingInterval)])),
        // filter the events to proceed only if the tiles are loaded
        filter(([tilesLoaded]) => tilesLoaded)
      )
      .subscribe(() => {
        // if the autorun is enabled, change the date index and update the layer
        if (this.autoRun && startIndex <= endIndex) {
          this.onDateChange(startIndex, false);
          startIndex++;
        }
        // if we reached the end of the list stop the autorun
        else if ((startIndex = endIndex)) {
          this._clearAutoRunPolling();
        }

        // repeat the cycle
        this._autorunPollingSubject.next();
      });
  }

  /**
   * Method to unsubscribe from the autorun polling process
   */
  private _unsubAutorunPolling(): void {
    if (this._autorunPollingSub) {
      this._autorunPollingSub.unsubscribe();
    }
  }
}
